/**
 * Format property measurements (Acres/Guntas) for display.
 *
 * Rules:
 *  - Don't show a unit if its value is 0
 *  - 0 Acres 20 Guntas  →  20 Guntas
 *  - 2 Acres 0 Guntas   →  2 Acres
 *  - 2 Acres 2 Guntas   →  2 Acres 2 Guntas
 */

/**
 * Build a display string from numeric acres + guntas values.
 * @param {number|string} acres
 * @param {number|string} guntas
 * @returns {string}
 */
const buildAcresGuntasString = (acres, guntas) => {
  const a = Number(acres) || 0;
  const g = Number(guntas) || 0;
  if (a === 0 && g > 0) {
    return `only ${g} Gunta${g !== 1 ? 's' : ''}`;
  }
  const parts = [];
  if (a > 0) parts.push(`${a} Acre${a !== 1 ? 's' : ''}`);
  if (g > 0) parts.push(`${g} Gunta${g !== 1 ? 's' : ''}`);
  return parts.join(' ');
};

/**
 * Parse an Acres/Guntas string like "0 Acres 20 Guntas" and return a clean display string.
 * @param {string} areaStr
 * @returns {string}
 */
const parseAcresGuntasString = (areaStr) => {
  const acresMatch = areaStr.match(/(\d+(?:\.\d+)?)\s*Acres?/i);
  const guntasMatch = areaStr.match(/(\d+(?:\.\d+)?)\s*Guntas?/i);
  const acres = acresMatch ? Number(acresMatch[1]) : 0;
  const guntas = guntasMatch ? Number(guntasMatch[1]) : 0;
  return buildAcresGuntasString(acres, guntas);
};

/**
 * Main display formatter — works for both admin-added and user-submitted properties.
 *
 * Priority:
 *  1. Dedicated numeric `acres` / `guntas` fields  (admin form always sends these)
 *  2. `farmhouseAreaAcres` + `guntas`              (Farmhouse via admin)
 *  3. `area` string that contains "Acres"/"Guntas" (user form fallback)
 *  4. Raw `area` string for all other property types
 *
 * @param {Object} property
 * @returns {string}
 */
export const formatAreaDisplay = (property) => {
  // 1. Dedicated acres/guntas numeric fields (most reliable)
  if (property.acres !== undefined || property.guntas !== undefined) {
    const result = buildAcresGuntasString(property.acres, property.guntas);
    if (result) return result;
    // Both are 0 — fall through to area string
  }

  // 2. Farmhouse uses farmhouseAreaAcres + guntas
  if (property.farmhouseAreaAcres !== undefined) {
    const result = buildAcresGuntasString(property.farmhouseAreaAcres, property.guntas);
    if (result) return result;
  }

  // 3. Area string that contains Acres/Guntas text
  if (property.area && typeof property.area === 'string' && /acre|gunta/i.test(property.area)) {
    const result = parseAcresGuntasString(property.area);
    if (result) return result;
  }

  // 4. Plain area string (sq.ft, sq yards, etc.)
  return property.area || 'N/A';
};

/**
 * Returns a measurement string for sharing.
 * Priority: acres/guntas → farmhouse acres → area string with acres/guntas text
 *           → plain numeric area (labelled by category) → raw area string.
 * Returns empty string only when there is truly no measurement data.
 * @param {Object} property
 * @returns {string}
 */
export const getShareExtent = (property) => {
  const a  = Number(property.acres) || 0;
  const g  = Number(property.guntas) || 0;
  const fa = Number(property.farmhouseAreaAcres) || 0;

  const fmtAcres = (ac, gu) => {
    const parts = [];
    if (ac > 0) parts.push(`${ac} Acre${ac !== 1 ? 's' : ''}`);
    if (gu > 0) parts.push(`${gu} Gunta${gu !== 1 ? 's' : ''}`);
    return parts.join(' ');
  };

  // 1. Numeric acres / guntas
  if (a > 0 || g > 0) return fmtAcres(a, g);

  // 2. Farmhouse acres + guntas
  if (fa > 0) return fmtAcres(fa, g);

  const areaStr = String(property.area || '').trim();

  // 3. Area string containing "Acres"/"Guntas" text
  if (/acre|gunta/i.test(areaStr)) {
    const am = areaStr.match(/(\d+(?:\.\d+)?)\s*Acres?/i);
    const gm = areaStr.match(/(\d+(?:\.\d+)?)\s*Guntas?/i);
    return fmtAcres(am ? Number(am[1]) : 0, gm ? Number(gm[1]) : 0);
  }

  // 4. Built-up / plot area for other property types
  if (property.builtUpArea) return `${property.builtUpArea} sq.ft`;
  if (property.buildupArea)  return `${property.buildupArea} sq.ft`;
  if (property.plotAreaSqYards) return `${property.plotAreaSqYards} sq.yards`;
  if (property.plotArea)     return `${property.plotArea} sq.yards`;

  // 5. Plain numeric area — label by category
  const num = parseFloat(areaStr);
  if (!isNaN(num) && num > 0) {
    const cat = property.category || '';
    if (cat === 'Agricultural Land' || cat === 'Farmhouse') return fmtAcres(num, 0);
    if (cat === 'Open Plot')                                 return `${num} sq.yards`;
    return `${areaStr} sq.ft`;
  }

  // 6. Raw area string as-is (e.g. "1000-1500 sq.ft", "2 Acres")
  if (areaStr && areaStr !== '0') return areaStr;

  return '';
};


export const getAreaUnit = (type) => {
  if (type === 'Agricultural Land' || type === 'Farmhouse') return 'Acres/Guntas';
  if (type === 'Open Plot') return 'Sq Yards';
  if (['Independent House', 'Apartment', 'Office / Commercial Space', 'Office Space'].includes(type)) return 'SFT';
  return '';
};
